import {
  ConstructedEvents,
  EventObject,
  MonitoredMediaEventList,
  MonitoredMediaEvents
} from './constants';
import {
  dispatchIfValidDuration,
  dispatchPlayerEvent
} from '../../events/dispatch';
import ContentConfig from '../../config/ContentConfig';
import PlayerElement, {
  playerElementAsMaybeMediaElement
} from '../../util/PlayerElement';
import BaseInteraction, { CleanupMethod } from '../../player/BaseInteraction';
import getEventForEventMetadata, {
  resEvent
} from '../../util/getEventForEventMetadata';
import logger from '../../log';
import transformDuration from './transformDuration';
import { BasePlayerEvent } from '../../reporter/constants';
import { Handler } from '@kollective-frontend/ksdk-api';
import { TechApi } from '../../bootloader/tech';

const log = logger.getLogger(__filename);

interface GetElementArgs<T> {
  player: PlayerElement;
  event?: Event;
  interaction?: BaseInteraction<T>;
}

// Exported for unit tests
export const getLatestElement = <T>(args: GetElementArgs<T>): PlayerElement => {
  const { event, player, interaction } = args;
  const target = playerElementAsMaybeMediaElement(event?.target) ?? null;
  if (!interaction && target) {
    return target;
  }
  if (!interaction) {
    return player;
  }
  return interaction.latestElement(target, player) as PlayerElement;
};

const isResizeObserverSupported = (element: unknown): boolean => {
  return (
    typeof window.ResizeObserver !== 'undefined' &&
    element instanceof HTMLElement
  );
};

export interface MuteCheck {
  mute?: boolean;
}

export function maybeAttachTownHall(
  player: PlayerElement,
  tech: TechApi,
  handlers: Map<string, Handler<Event>>,
  interaction?: BaseInteraction<unknown>
): void {
  const isTeams = ContentConfig.getInstance().app === 'microsoft.teams';

  if (!isTeams || !interaction) {
    // No interaction means a Teams Live Event
    return;
  }
  const state: MuteCheck = {};
  const handler = generateMSVolumeEventHandler(player, tech, state);
  handlers.set('timeupdate', handler);
  player.addEventListener('timeupdate', handler);
}

export function generateMSVolumeEventHandler(
  player: PlayerElement,
  tech: TechApi,
  state: MuteCheck
): Handler<unknown> {
  const { volumechange } = MonitoredMediaEvents;
  return (event: unknown) => {
    const evt = event as Event;
    const res = getEventForEventMetadata(volumechange, evt, player, tech);
    if (typeof res?.mute === 'undefined') {
      return;
    }
    if (res.mute !== state.mute) {
      dispatchPlayerEvent(res);
      state.mute = res.mute;
    }
  };
}

/**
 * Create a resize observer so we can tell when the video element changes size.
 *
 * @param element - HTMLElement (video)
 * @param callback - action we want to call on the resize
 * @returns an observer if the browser supports it.
 */
export const createResizeObserver = (
  element: PlayerElement,
  callback: ResizeObserverCallback
): ResizeObserver | undefined => {
  if (!isResizeObserverSupported(element)) {
    return;
  }
  const resizeObserver = new ResizeObserver(callback);
  resizeObserver.observe(element as HTMLElement);
  return resizeObserver;
};

interface ResizeHandlerState {
  evt?: BasePlayerEvent;
  last: number;
  counter: number;
}

export const MIN_CLIENT_RES_DELAY = 1000;

/**
 * Resize Handler Logic: No more than once per MIN_CLIENT_RES_DELAY ms send a
 * received client resolution change event to the back end. However, this will
 * make it so that we will miss the last event a series of events that fire
 * several times per second. In order to get that last event, set a
 * MIN_CLIENT_RES_DELAY timeout each time we get an event that is less than a
 * MIN_CLIENT_RES_DELAY ms from the previous one and increment a counter and
 * set a flag variable to that same counter. When each timeout completes, only
 * dispatch an event if there have been no client resolution change events in
 * the last MIN_CLIENT_RES_DELAY ms.
 *
 * Example behavior: If the MIN_CLIENT_RES_DELAY is one second.
 * If the size of the client changes several times a second over 2+ seconds,
 * the first event will be emitted, and approximately every second another event
 * will be emittted. When the events stop, the very last event will have the
 * same count as the counter and will emit an event so we would see:
 *
 * - Event 1 - sent. Events 2-25 occur before a second has passed
 * - Event 26 (more than 1 second after 1st) - sent. Events: 27-50 before 2 sec.
 * - Event 51 (more than 2 seconds after 1st) - sent. Events: 52-78
 * - Event 79 (last event 2.33 seconds after 1st) - sent.
 *
 * @returns The custom handler to emit resize events.
 */
export const generateResizeHandler = (): ResizeObserverCallback => {
  const e = ConstructedEvents.resolutionchanged.code;
  const state: ResizeHandlerState = { last: 0, counter: 0 };
  return (roe: ResizeObserverEntry[]) => {
    if (roe.length < 1) {
      return;
    }

    const ts = Date.now();
    const { height, width } = roe[0]?.contentRect ?? {};
    const cr = `${width}x${height}`;
    const evt = resEvent(height, width, { e, ts, cr }, 'generateResizeHandler');
    if (!evt) {
      return;
    }
    if (ts - state.last >= MIN_CLIENT_RES_DELAY) {
      state.last = ts;
      dispatchPlayerEvent(evt);
      return;
    }
    state.counter++;
    const count = state.counter;
    setTimeout(() => {
      if (count != state.counter) {
        return;
      }
      state.last = Date.now();
      dispatchPlayerEvent(evt);
    }, MIN_CLIENT_RES_DELAY);
  };
};

const attachVideoElement = <T>(
  player: PlayerElement,
  tech: TechApi,
  interaction?: BaseInteraction<T>
): CleanupMethod => {
  const generateHandler =
    (eo: EventObject): Handler<Event> =>
    (event: Event): void => {
      const p = getLatestElement({ player, event, interaction });
      const dispatchable = getEventForEventMetadata(eo, event, p, tech);
      if (dispatchable) {
        log.info('player event:', event);
        dispatchPlayerEvent(dispatchable);
      }
    };

  // Try setting the duration immediately upon attachment
  dispatchIfValidDuration(transformDuration(player.duration));

  const handlers: Map<string, Handler<Event>> = new Map();
  Object.getOwnPropertyNames(MonitoredMediaEvents)
    .map(key => MonitoredMediaEvents[key as MonitoredMediaEventList])
    .forEach(event => {
      const handler = generateHandler(event);
      handlers.set(event.name, handler);
      player.addEventListener(event.name, handler as Handler<unknown>);
    });

  // Have to do something special for resizing the video element
  const resizeObserver = createResizeObserver(player, generateResizeHandler());

  maybeAttachTownHall(player, tech, handlers, interaction);

  return () => {
    if (typeof player.removeEventListener !== 'function') {
      log.debug('Not able to unset handlers', player);
    } else {
      log.debug('Unsetting handlers', player);
      handlers.forEach((handler, name) => {
        player.removeEventListener?.(name, handler as Handler<unknown>);
      });
    }
    resizeObserver?.unobserve(player as HTMLElement);
    handlers.clear();
  };
};

export default attachVideoElement;
